import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { ArrowLeft, Plus, Users, MapPin, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import InviteToOrgModal from './InviteToOrgModal';
import { ShopifyConnectionSection } from '@/features/organization/components/ShopifyConnectionSection';
import { StoreLeftToDo, readCreateStoreFailure } from './StoreSetupStatus';

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: org, isLoading, error } = trpc.platform.getOrganization.useQuery(
    { organizationId: id! },
    { enabled: !!id },
  );
  // Started here, in parallel with getOrganization, so the Shopify card does
  // not wait for the page's early return. Same key as the card's own query:
  // React Query dedupes it into one request.
  // Its data also feeds the Left to do card (no extra query).
  const { data: connection } = trpc.platform.getShopifyConnection.useQuery(
    { organizationId: id! },
    { enabled: !!id },
  );

  if (isLoading) {
    return (
      <div className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/platform/stores')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="text-center py-20 text-destructive">
          Store not found
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/platform/stores')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Stores
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{org.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-muted-foreground">{org.slug}</span>
              {org.shopifyDomain && (
                <>
                  <span className="text-muted-foreground">&middot;</span>
                  <span className="text-sm text-muted-foreground">{org.shopifyDomain}</span>
                </>
              )}
              {org.isActive ? (
                <Badge variant="default" className="bg-green-600 text-white text-xs ml-2">Active</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs ml-2">Inactive</Badge>
              )}
            </div>
          </div>
        </div>

        <StoreLeftToDo
          setupRequired={org.setupRequired}
          setupCompletedAt={org.setupCompletedAt}
          connection={connection}
          members={org.members}
          hasPendingAdminInvite={org.hasPendingAdminInvite}
          failure={readCreateStoreFailure(location.state)}
        />

        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-4 w-4" /> Members ({org.members.length})
            </CardTitle>
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Invite User
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {org.members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No members yet
                    </TableCell>
                  </TableRow>
                ) : (
                  org.members.map(member => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.firstName} {member.lastName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{member.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{member.role}</Badge>
                      </TableCell>
                      <TableCell>
                        {member.isActive ? (
                          <Badge variant="default" className="bg-green-600 text-white text-xs">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Store className="h-4 w-4" aria-hidden="true" /> Shopify
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ShopifyConnectionSection organizationId={id!} mode="platform" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-4 w-4" /> Locations ({org.locations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {org.locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No locations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  org.locations.map(loc => (
                    <TableRow key={loc.id}>
                      <TableCell className="font-medium">{loc.name}</TableCell>
                      <TableCell className="text-muted-foreground">{loc.city || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{loc.country_code || '-'}</TableCell>
                      <TableCell>
                        {loc.is_active ? (
                          <Badge variant="default" className="bg-green-600 text-white text-xs">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <InviteToOrgModal
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          organizationId={id!}
          organizationName={org.name}
        />
      </main>
    </div>
  );
}
